import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, Bot, CheckCircle2, Clock3, Play, RefreshCw, ScanSearch, Server, TriangleAlert } from 'lucide-react';
import { toast } from 'react-toastify';
import { api } from '../lib_api';

type DeskHealth = {
  status: string; healthy: boolean; expectedNow: boolean; intervalSeconds: number;
  evaluatedAt: string | null; ageSeconds: number | null; strategyCount?: number;
  expectedStrategyCount?: number; marketCount: number; scannedCount?: number;
  expectedMarketCount: number; activeSessions?: string[]; blockers: string[];
};
type AccountHealth = {
  tradingAccountId: number; userEmail: string; accountLabel: string; broker: string;
  workerStatus: string; workerHealthy: boolean; snapshotAgeSeconds: number | null;
  manual: DeskHealth; ai: DeskHealth;
};
type DeskRollup = { status: string; healthyAccounts: number; totalAccounts: number; latestEvaluationAt: string | null };
type HealthResponse = {
  checkedAt: string; overallStatus: string;
  expected: { strategies: number; markets: string[]; manualIntervalSeconds: number; aiIntervalSeconds: number };
  workers: { healthy: number; total: number }; manual: DeskRollup; ai: DeskRollup; accounts: AccountHealth[];
};

const statusStyles: Record<string, string> = {
  healthy: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30',
  scheduled: 'bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/30',
  degraded: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30',
  stale: 'bg-orange-50 text-orange-700 ring-orange-200 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-500/30',
  offline: 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/30',
};

function StatusBadge({ status }: { status: string }) {
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize ring-1 ${statusStyles[status] || statusStyles.degraded}`}>{status}</span>;
}
function elapsed(seconds: number | null) {
  if (seconds === null) return 'never';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}
function localTime(value: string | null) {
  if (!value) return 'No evaluation received';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'medium' }).format(new Date(value));
}

function DeskSummary({ name, icon, data, cadence, coverage }: { name: string; icon: React.ReactNode; data?: DeskRollup; cadence: string; coverage: string }) {
  const status = data?.status || 'offline';
  return (
    <section className="panel p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-lg bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">{icon}</span>
          <div><p className="text-xs font-medium uppercase text-gray-500 dark:text-darkTextSecondary">Evaluation desk</p><h2 className="mt-0.5 text-lg font-semibold text-gray-950 dark:text-white">{name}</h2></div>
        </div>
        <StatusBadge status={status} />
      </div>
      <div className="mt-5 grid grid-cols-3 gap-3 border-t border-gray-200 pt-4 dark:border-white/10">
        <div><p className="text-xs text-gray-500 dark:text-darkTextSecondary">Accounts</p><p className="mt-1 font-semibold">{data?.healthyAccounts || 0}/{data?.totalAccounts || 0} healthy</p></div>
        <div><p className="text-xs text-gray-500 dark:text-darkTextSecondary">Cadence</p><p className="mt-1 font-semibold">{cadence}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-darkTextSecondary">Coverage</p><p className="mt-1 font-semibold">{coverage}</p></div>
      </div>
      <p className="mt-4 flex items-center gap-2 text-xs text-gray-500 dark:text-darkTextSecondary"><Clock3 size={14} /> Latest: {localTime(data?.latestEvaluationAt || null)}</p>
    </section>
  );
}

function DeskCell({ desk, kind }: { desk: DeskHealth; kind: 'manual' | 'ai' }) {
  const detail = kind === 'manual'
    ? `${desk.scannedCount || 0}/${desk.expectedMarketCount} markets · ${desk.strategyCount || 0}/${desk.expectedStrategyCount || 12} strategies`
    : `${desk.marketCount || 0}/${desk.expectedMarketCount} eligible markets`;
  return (
    <div className="min-w-[210px]">
      <div className="flex items-center gap-2"><StatusBadge status={desk.status} /><span className="text-xs text-gray-500 dark:text-darkTextSecondary">{elapsed(desk.ageSeconds)}</span></div>
      <p className="mt-2 text-sm font-medium text-gray-900 dark:text-white">{detail}</p>
      {desk.blockers.length ? <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">{desk.blockers[0]}</p> : <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">Cadence and coverage confirmed</p>}
    </div>
  );
}

export default function RuntimeMonitor() {
  const queryClient = useQueryClient();
  const health = useQuery<HealthResponse>({ queryKey: ['admin', 'trading-desk-health'], queryFn: async () => (await api.get('/api/admin/trading-desk-health')).data, refetchInterval: 30000 });
  const check = useMutation({
    mutationFn: async () => (await api.post('/api/admin/trading-desk-health/check')).data as HealthResponse,
    onSuccess: (data) => {
      queryClient.setQueryData(['admin', 'trading-desk-health'], data);
      toast.success(data.overallStatus === 'healthy' ? 'Both trading desks passed the live check.' : 'Check complete. Review the flagged accounts.');
    },
    onError: () => toast.error('The live health check could not be completed.'),
  });
  const data = health.data;
  const loading = health.isLoading || check.isPending;
  return (
    <div className="space-y-4">
      <section className="panel flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"><Activity size={22} /></span>
          <div>
            <div className="flex flex-wrap items-center gap-2"><h1 className="text-xl font-semibold text-gray-950 dark:text-white">Trading engine health</h1><StatusBadge status={data?.overallStatus || 'offline'} /></div>
            <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-darkTextSecondary">Confirms workers, live ticks, all 12 strategies, and both evaluation loops are current.</p>
            <p className="mt-2 text-xs text-gray-500 dark:text-darkTextSecondary">Refreshes every 30 seconds · Last checked {localTime(data?.checkedAt || null)}</p>
          </div>
        </div>
        <button type="button" className="btn min-w-[190px] bg-indigo-600 hover:bg-indigo-700" disabled={loading} onClick={() => check.mutate()}>{loading ? <RefreshCw size={17} className="animate-spin" /> : <Play size={17} />}{loading ? 'Checking live engines' : 'Run live health check'}</button>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <DeskSummary name="AI Trade Center" icon={<Bot size={20} />} data={data?.ai} cadence={`${Math.round((data?.expected.aiIntervalSeconds || 300) / 60)} min`} coverage={`${data?.expected.markets.length || 6} markets`} />
        <DeskSummary name="Manual Trading" icon={<ScanSearch size={20} />} data={data?.manual} cadence={`${data?.expected.manualIntervalSeconds || 60} sec`} coverage={`${data?.expected.strategies || 12} strategies`} />
      </div>

      <section className="panel overflow-hidden">
        <div className="flex flex-col gap-2 border-b border-gray-200 px-5 py-4 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
          <div><h2 className="font-semibold text-gray-950 dark:text-white">Active account checks</h2><p className="mt-1 text-xs text-gray-500 dark:text-darkTextSecondary">Healthy requires a running worker, fresh MT5 data, and complete evaluation coverage.</p></div>
          <div className="flex items-center gap-2 text-sm"><Server size={16} /><span>{data?.workers.healthy || 0}/{data?.workers.total || 0} workers healthy</span></div>
        </div>
        {health.isError ? (
          <div className="flex items-center gap-3 p-6 text-rose-700 dark:text-rose-300"><TriangleAlert size={20} /><span>Runtime health could not be loaded.</span></div>
        ) : !data?.accounts.length ? (
          <div className="p-8 text-center"><TriangleAlert className="mx-auto text-amber-500" /><p className="mt-3 font-medium">No connected active trading account was found.</p><p className="mt-1 text-sm text-gray-500 dark:text-darkTextSecondary">Connect an MT5 account, then run the check again.</p></div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500 dark:bg-white/5 dark:text-darkTextSecondary"><tr><th className="px-5 py-3">Account</th><th className="px-5 py-3">Worker</th><th className="px-5 py-3">AI center</th><th className="px-5 py-3">Manual</th></tr></thead>
            <tbody className="divide-y divide-gray-200 dark:divide-white/10">{data.accounts.map((account) => (
              <tr key={account.tradingAccountId} className="align-top">
                <td className="px-5 py-4"><p className="font-semibold text-gray-950 dark:text-white">{account.broker}</p><p className="mt-1 text-xs">{account.accountLabel}</p><p className="mt-1 text-xs text-gray-500 dark:text-darkTextSecondary">{account.userEmail}</p></td>
                <td className="px-5 py-4"><div className="flex items-center gap-2">{account.workerHealthy ? <CheckCircle2 size={17} className="text-emerald-600" /> : <TriangleAlert size={17} className="text-amber-600" />}<span className="font-medium capitalize">{account.workerStatus.replaceAll('_', ' ')}</span></div><p className="mt-2 text-xs text-gray-500 dark:text-darkTextSecondary">Snapshot {elapsed(account.snapshotAgeSeconds)}</p></td>
                <td className="px-5 py-4"><DeskCell desk={account.ai} kind="ai" /></td><td className="px-5 py-4"><DeskCell desk={account.manual} kind="manual" /></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </section>
    </div>
  );
}
